import { useCallback, useEffect, useRef, useState } from 'react';
import type { Notification } from '@court-sync/shared';
import { notificationService } from '@/services/notification';

const NOTIFICATIONS_POLL_INTERVAL_MS = 20_000;
/** Hide the unread badge after the panel stays open this long (user has seen the list). */
const NOTIFICATION_BADGE_AUTO_DISMISS_MS = 2_500;
let lastNotificationsFetchTime = 0;

function formatTimeAgo(date: Date | string | { toDate?: () => Date }): string {
  const now = new Date();
  let notificationDate: Date;

  if (date && typeof date === 'object' && 'toDate' in date && date.toDate) {
    notificationDate = date.toDate();
  } else if (typeof date === 'string') {
    notificationDate = new Date(date);
  } else if (date instanceof Date) {
    notificationDate = date;
  } else {
    return 'Just now';
  }

  const diffInSeconds = Math.floor((now.getTime() - notificationDate.getTime()) / 1000);

  if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} min ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
  return notificationDate.toLocaleDateString();
}

export function useNotificationsPanel(userId: string | undefined) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isClearingNotificationsRef = useRef(false);
  const isPanelOpenRef = useRef(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadNotifications = useCallback(async () => {
    if (!userId) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    lastNotificationsFetchTime = Date.now();
    try {
      if (notifications.length === 0) {
        setLoading(true);
      }

      const response = await notificationService.getNotifications(1, 10);
      if (response.success && response.data) {
        setNotifications(response.data.notifications || []);

        if (!isClearingNotificationsRef.current) {
          const unread = response.data.notifications.filter((n: Notification) => !n.isRead).length;
          setUnreadCount(unread);
        }
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, notifications.length]);

  useEffect(() => {
    if (!userId) return undefined;

    const now = Date.now();
    const shouldFetchNow = now - lastNotificationsFetchTime >= NOTIFICATIONS_POLL_INTERVAL_MS;
    if (shouldFetchNow) {
      void loadNotifications();
    }
    const interval = setInterval(() => void loadNotifications(), NOTIFICATIONS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userId, loadNotifications]);

  const handleMarkAllAsRead = useCallback(async () => {
    if (unreadCount === 0) return;

    try {
      isClearingNotificationsRef.current = true;
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setTimeout(() => {
        isClearingNotificationsRef.current = false;
      }, 3000);
    }
  }, [unreadCount]);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const handlePanelOpenChange = useCallback(
    (open: boolean) => {
      isPanelOpenRef.current = open;
      clearDismissTimer();

      if (open && unreadCount > 0) {
        dismissTimerRef.current = setTimeout(() => {
          if (isPanelOpenRef.current) {
            void handleMarkAllAsRead();
          }
        }, NOTIFICATION_BADGE_AUTO_DISMISS_MS);
      }
    },
    [clearDismissTimer, handleMarkAllAsRead, unreadCount]
  );

  useEffect(() => () => clearDismissTimer(), [clearDismissTimer]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      try {
        await notificationService.markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    formatTimeAgo,
    loadNotifications,
    handleMarkAllAsRead,
    handleNotificationClick,
    handlePanelOpenChange,
  };
}
